import React, { useState, useCallback, useEffect, useMemo } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { showLoader, hideLoader } from '../../redux/loader/actions';
import menuApi from '../../services/menu';
import { setMenuItems } from '../../redux/menu/actions';
import { setEncryptedCookie, getDecryptedCookie, removeCookie } from '../../utils';
import BusinessSelection from '../signin/business_selection';
import { Dialog } from '@mui/material';
import authApi from '../../services/auth';
import userApi from '../../services/users';
import { showSnackbar } from '../../redux/snackbar/actions';

const Navbar = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [currentMenu, setCurrentMenu] = useState(null);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [selectedOption, setSelectedOption] = useState('');

    const menuItems = useSelector((state) => state.menuReducer.items.filter((item) => item.access_type !== 'none'));

    const id = getDecryptedCookie('id');
    const rt = getDecryptedCookie('rt');
    const at = getDecryptedCookie('at');

    const getAllMenu = useCallback(
        (id) => {
            if (!id) return;
            dispatch(showLoader());
            const onSuccess = (res) => {
                dispatch(hideLoader());
                if (res?.data?.status) {
                    const items = res.data.data || [];
                    const sortedItems = items.sort((a, b) => a.menu_order - b.menu_order);
                    dispatch(setMenuItems(sortedItems));
                }
            };
            const onFailure = (err) => {
                dispatch(hideLoader());
                console.error('Error fetching menu:', err);
            };
            menuApi.userBasedMenu(id).then(onSuccess, onFailure);
        },
        [dispatch]
    );

    useEffect(() => {
        if (id) {
            getAllMenu(id);
        }
    }, [id, getAllMenu]);

    const handleClick = (event, menuName) => {
        setAnchorEl(event.currentTarget);
        setCurrentMenu(menuName);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setCurrentMenu(null);
    };

    const handleDrawerToggle = () => {
        setDrawerOpen(!drawerOpen);
    };

    const handleDropdownToggle = (menuName) => {
        setOpenDropdown((prev) => (prev === menuName ? null : menuName));
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
    };

    const isActive = (link, menuName, submenus = []) => {
        const normalizedLink = link?.toLowerCase();
        const normalizedPathname = location.pathname?.toLowerCase();

        const isSubscriptionsMenu = menuName?.toLowerCase() === 'subscriptions';
        const isSubscriptionsPath = normalizedPathname.startsWith('/subscriptions');

        const isSubmenuActive = submenus.some((submenu) => submenu.link?.toLowerCase() === normalizedPathname);

        if (isSubscriptionsMenu && isSubscriptionsPath) {
            return { fontWeight: 'bold', color: '#a00' };
        }

        if (normalizedPathname === normalizedLink || isSubmenuActive) {
            return { fontWeight: 'bold', color: '#a00' };
        }

        return {};
    };

    const memoizedMenuItems = useMemo(() => {
        return [...menuItems]
            .map((menu) => ({
                ...menu,
                submenus: menu.submenus ? [...menu.submenus].sort((a, b) => a.menu_order - b.menu_order) : []
            }))
            .sort((a, b) => a.menu_order - b.menu_order);
    }, [menuItems]);

    // useEffect(() => {
    //   const intervalId = setInterval(() => {
    //     if (memoizedMenuItems.length <= 0) {
    //       window.location.reload();
    //     }
    //   }, 3000);

    //   return () => clearInterval(intervalId);
    // }, [memoizedMenuItems]);

    const editUserById = async (userId) => {
        dispatch(showLoader());
        try {
            const response = await userApi.getUserId(userId);
            removeCookie('uuid');
            if (response?.data?.status) {
                setEncryptedCookie('abc', response.data.data[0].role.uuid);
                setEncryptedCookie('name', response.data.data[0].user.organization_name);
                setEncryptedCookie('fdf', response.data.data[0].user.username, {
                    expires: 7
                });
                setEncryptedCookie('uuid', response.data.data[0].user.uuid);
                getAllMenu(response.data.data[0].role.uuid);
                const id = response.data.data[0].role.uuid;
                setEncryptedCookie('id', id, { expires: 7 });

                // Update session if needed
                sessionStorage.setItem('organisationId', response.data.data[0].user.uuid);
            } else {
                console.error('Error fetching user data');
            }
        } catch (error) {
            console.error('Error fetching user:', error);
        } finally {
            dispatch(hideLoader());
        }
    };

    const [verifyModal, setVerifyModal] = useState(false);
    const validateUserToken = () => {
        return new Promise((resolve) => {
            dispatch(showLoader());
            const onSuccess = (res) => {
                dispatch(hideLoader());
                if (res?.data?.status === true) {
                    sessionStorage.setItem('organisationId', res?.data?.uuid);
                    editUserById(res?.data?.uuid);
                    if (!res?.data?.has_access_in_current_id && res?.data?.has_access_in_other_id) {
                        setVerifyModal(true);
                        resolve(false);
                    } else {
                        resolve(true);
                    }
                } else {
                    resolve(false);
                }
            };
            const onFailure = (err) => {
                dispatch(hideLoader());
                resolve(false);
            };
            authApi.validateToken().then(onSuccess, onFailure);
        });
    };

    const handleMenuItemClick = async (link, callback) => {
        try {
            const isValid = await validateUserToken();
            if (isValid) {
                navigate(link);
                if (callback) callback();
            } else {
                console.error('Error validating token');
            }
        } catch (error) {
            console.error('Error validating token:', error);
            dispatch(
                showSnackbar({
                    message: 'Error validating session. Please try again.',
                    variant: 'error'
                })
            );
        }
    };

    return (
        <>
            <AppBar position='static' sx={{ backgroundColor: 'white', color: 'black' }}>
                <Toolbar sx={{ justifyContent: { xs: 'space-between', md: 'center' } }}>
                    <IconButton
                        edge='start'
                        color='inherit'
                        aria-label='menu'
                        onClick={handleDrawerToggle}
                        sx={{ display: { xs: 'block', md: 'none' } }}
                    >
                        <MenuIcon sx={{ color: 'black' }} />
                    </IconButton>

                    {memoizedMenuItems.length > 0 &&
                        memoizedMenuItems.map((menu) =>
                            menu.dropdown ? (
                                <div key={menu.id} style={{ position: 'relative' }}>
                                    <Button
                                        color='inherit'
                                        aria-controls={currentMenu === menu.name ? 'menu-list' : undefined}
                                        aria-haspopup='true'
                                        aria-expanded={currentMenu === menu.name ? 'true' : undefined}
                                        onClick={(event) => handleClick(event, menu.name)}
                                        sx={{
                                            display: { xs: 'none', md: 'flex' },
                                            mx: 2,
                                            color: 'black',
                                            whiteSpace: 'nowrap',
                                            alignItems: 'center',
                                            ...isActive(menu.link, menu.name, menu.submenus)
                                        }}
                                    >
                                        {menu.name}
                                        <ArrowDropDownIcon sx={{ ml: 1, color: 'black' }} />
                                    </Button>
                                    <Menu
                                        id='menu-list'
                                        anchorEl={anchorEl}
                                        open={currentMenu === menu.name}
                                        onClose={handleClose}
                                        sx={{
                                            '& .MuiMenuItem-root': {
                                                color: 'black'
                                            }
                                        }}
                                    >
                                        {menu.submenus.map((item) => (
                                            <MenuItem
                                                key={item.id}
                                                onClick={async () => {
                                                    await handleMenuItemClick(item.link, () => {
                                                        handleClose();
                                                        closeDrawer();
                                                    });
                                                }}
                                                component={Link}
                                                to={item.link}
                                                sx={isActive(item.link, item.name)}
                                            >
                                                {item.name}
                                            </MenuItem>
                                        ))}
                                    </Menu>
                                </div>
                            ) : (
                                <Button
                                    key={menu.id}
                                    color='inherit'
                                    component={Link}
                                    to={menu.link}
                                    sx={{
                                        display: { xs: 'none', md: 'block' },
                                        mx: 2,
                                        color: 'black',
                                        ...isActive(menu.link, menu.name)
                                    }}
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        await handleMenuItemClick(menu.link, closeDrawer);
                                    }}
                                >
                                    {menu.name}
                                </Button>
                            )
                        )}
                </Toolbar>
            </AppBar>

            <Drawer
                anchor='right'
                open={drawerOpen}
                onClose={handleDrawerToggle}
                sx={{
                    width: 250,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: 250,
                        backgroundColor: 'white',
                        color: 'black'
                    }
                }}
            >
                <List>
                    {memoizedMenuItems.map((menu) => (
                        <div key={menu.id}>
                            {menu.submenus && menu.submenus.length > 0 ? (
                                <div>
                                    <ListItem button onClick={() => handleDropdownToggle(menu.name)}>
                                        <Button
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: '100%'
                                            }}
                                        >
                                            {menu.name}
                                            <ArrowDropDownIcon sx={{ ml: 1, color: 'black' }} />
                                        </Button>
                                    </ListItem>
                                    <List
                                        sx={{
                                            display: openDropdown === menu.name ? 'block' : 'none'
                                        }}
                                    >
                                        {menu.submenus.map((item) => (
                                            <ListItem
                                                key={item.id}
                                                button
                                                component={Link}
                                                to={item.link}
                                                sx={isActive(item.link, item.name)}
                                                onClick={async (e) => {
                                                    e.preventDefault();
                                                    await handleMenuItemClick(item.link, closeDrawer);
                                                }}
                                            >
                                                <Button sx={{ color: 'black', width: '100%' }}>{item.name}</Button>
                                            </ListItem>
                                        ))}
                                    </List>
                                </div>
                            ) : (
                                <ListItem
                                    button
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        await handleMenuItemClick(menu.link, closeDrawer);
                                    }}
                                >
                                    <Button
                                        component={Link}
                                        to={menu.link}
                                        sx={{
                                            color: 'black',
                                            width: '100%',
                                            ...isActive(menu.link, menu.name)
                                        }}
                                    >
                                        {menu.name}
                                    </Button>
                                </ListItem>
                            )}
                        </div>
                    ))}
                </List>
            </Drawer>
            <Dialog open={verifyModal} maxWidth='sm' fullWidth>
                <BusinessSelection
                    BackdropProps={{
                        sx: {
                            backdropFilter: 'blur(6px)', // You can adjust the blur intensity
                            backgroundColor: 'rgba(0, 0, 0, 0.4)' // Optional: add a semi-transparent overlay
                        }
                    }}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                />
            </Dialog>
        </>
    );
};

export default Navbar;
